import React, { useCallback, useEffect, useRef, useState } from "react";
import "./LoginScreen.css";
import { flags } from "./shared/featureFlags";
import { loadTurnstileScript } from "./turnstileLoader";

const modes = { signIn: "signIn", signUp: "signUp", forgot: "forgot", reset: "reset", verify: "verify" };
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const TURNSTILE_ACTIVE = flags.turnstile && Boolean(TURNSTILE_SITE_KEY);

// Matches the "Password requirements" copy shown in the signup form below:
// at least 8 characters, at least one letter, at least one number.
export function isValidPassword(password) {
  const value = String(password || "");
  return value.length >= 8 && /[A-Za-z]/.test(value) && /[0-9]/.test(value);
}

function authMessage(error) {
  const raw = String(error?.message || error || "").toLowerCase();
  if (raw.includes("invalid login") || raw.includes("invalid credentials")) return "The email or password is not correct.";
  if (raw.includes("email not confirmed") || raw.includes("not confirmed")) return "Verify your email before signing in. We can send a fresh verification link.";
  if (raw.includes("expired") || raw.includes("invalid token") || raw.includes("otp_expired")) return "This link has expired or is no longer valid. Request a new email to continue.";
  if (raw.includes("rate limit") || raw.includes("too many")) return "Too many attempts. Wait a few minutes, then try again.";
  if (raw.includes("captcha") || raw.includes("turnstile")) return TURNSTILE_ACTIVE ? "The security check could not be completed. Retry the security check, then try again." : "Authentication could not be completed. Please try again.";
  if (raw.includes("failed to fetch") || raw.includes("network")) return "Nexus is having trouble connecting. Check your connection and try again.";
  if (raw.includes("banned") || raw.includes("disabled")) return "This Nexus account is disabled. Contact a Nexus administrator.";
  return error?.message || "We could not complete that request. Please try again.";
}

const TURNSTILE_WATCHDOG_MS = 45000;
const TURNSTILE_INTERACTIVE_WATCHDOG_MS = 60000;
const TURNSTILE_MAX_AUTOMATIC_RESETS = 2;

function friendlyTurnstileMessage(status) {
  if (status === "error" || status === "script-error") return "We couldn't load the security check. Check your connection and try again.";
  if (status === "unsupported") return "This browser cannot run the security check. Try an up-to-date browser.";
  if (status === "timeout") return "The security check timed out.";
  if (status === "expired") return "The security check expired. A new check is starting.";
  if (status === "stuck") return "The security check is taking longer than expected.";
  return "Security check in progress…";
}

export function Turnstile({ onToken, onStateChange, resetSignal = 0 }) {
  const nodeRef = useRef(null);
  const widgetRef = useRef(null);
  const cancelledRef = useRef(false);
  const renderInProgressRef = useRef(false);
  const watchdogRef = useRef(null);
  const watchdogDeadlineRef = useRef(null);
  const interactiveStartedRef = useRef(false);
  const renderRef = useRef(null);
  const resetWidgetRef = useRef(null);
  const statusRef = useRef(TURNSTILE_SITE_KEY ? "loading" : "unsupported");
  const automaticResetCountRef = useRef(0);
  const [status, setStatus] = useState(TURNSTILE_SITE_KEY ? "loading" : "unsupported");
  const [errorCode, setErrorCode] = useState("");

  const updateStatus = useCallback((next, code = "") => {
    statusRef.current = next;
    setStatus(next);
    setErrorCode(code ? String(code) : "");
    onStateChange?.(next);
  }, [onStateChange]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!TURNSTILE_SITE_KEY || !nodeRef.current) {
      onToken("");
      onStateChange?.("unsupported");
      return undefined;
    }

    const clearWatchdog = () => {
      if (watchdogRef.current) window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    };
    const startWatchdog = (delay = TURNSTILE_WATCHDOG_MS, forceNewDeadline = false) => {
      clearWatchdog();
      if (forceNewDeadline || watchdogDeadlineRef.current === null) watchdogDeadlineRef.current = Date.now() + delay;
      const remaining = Math.max(0, watchdogDeadlineRef.current - Date.now());
      watchdogRef.current = window.setTimeout(() => {
        if (cancelledRef.current || statusRef.current === "solved") return;
        updateStatus("stuck");
        onToken("");
      }, remaining);
    };
    const removeWidget = () => {
      clearWatchdog();
      if (widgetRef.current !== null && window.turnstile?.remove) {
        try { window.turnstile.remove(widgetRef.current); } catch (_) { /* best-effort unmount cleanup */ }
      }
      widgetRef.current = null;
      watchdogDeadlineRef.current = null;
      interactiveStartedRef.current = false;
      if (nodeRef.current) nodeRef.current.replaceChildren();
    };
    const resetWidget = ({ fresh = false } = {}) => {
      if (widgetRef.current === null || !window.turnstile?.reset) return false;
      if (fresh) {
        watchdogDeadlineRef.current = null;
        interactiveStartedRef.current = false;
      }
      try {
        window.turnstile.reset(widgetRef.current);
        startWatchdog();
        return true;
      } catch (_) { return false; }
    };
    const render = () => {
      if (cancelledRef.current || renderInProgressRef.current || widgetRef.current !== null || !nodeRef.current || !window.turnstile) return;
      renderInProgressRef.current = true;
      onToken("");
      watchdogDeadlineRef.current = null;
      interactiveStartedRef.current = false;
      updateStatus("ready");
      try {
        widgetRef.current = window.turnstile.render(nodeRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          retry: "auto",
          "retry-interval": 8000,
          "refresh-expired": "auto",
          "refresh-timeout": "auto",
          callback: (token) => {
            if (cancelledRef.current) return;
            clearWatchdog();
            watchdogDeadlineRef.current = null;
            interactiveStartedRef.current = false;
            updateStatus("solved");
            onToken(token);
          },
          "error-callback": (code) => {
            if (cancelledRef.current) return;
            onToken("");
            updateStatus("error", code);
            if (automaticResetCountRef.current < TURNSTILE_MAX_AUTOMATIC_RESETS) {
              automaticResetCountRef.current += 1;
              updateStatus("recovering", code);
              if (!resetWidget()) updateStatus("error", code);
            }
          },
          "expired-callback": () => {
            if (cancelledRef.current) return;
            onToken("");
            updateStatus("expired");
            startWatchdog();
          },
          "timeout-callback": () => {
            if (cancelledRef.current) return;
            onToken("");
            updateStatus("timeout");
            if (automaticResetCountRef.current < TURNSTILE_MAX_AUTOMATIC_RESETS) {
              automaticResetCountRef.current += 1;
              updateStatus("recovering");
              if (!resetWidget()) updateStatus("timeout");
            }
          },
          "before-interactive-callback": () => {
            if (cancelledRef.current) return;
            updateStatus("interactive");
            if (!interactiveStartedRef.current) {
              interactiveStartedRef.current = true;
              startWatchdog(TURNSTILE_INTERACTIVE_WATCHDOG_MS, true);
            } else startWatchdog(TURNSTILE_INTERACTIVE_WATCHDOG_MS);
          },
          "after-interactive-callback": () => {
            if (!cancelledRef.current && statusRef.current !== "solved") updateStatus("interactive");
          },
          "unsupported-callback": () => {
            if (cancelledRef.current) return;
            clearWatchdog();
            onToken("");
            updateStatus("unsupported");
          },
        });
        renderInProgressRef.current = false;
        startWatchdog();
      } catch (error) {
        renderInProgressRef.current = false;
        onToken("");
        updateStatus("error", error?.name || "render_error");
      }
    };

    renderRef.current = render;
    resetWidgetRef.current = resetWidget;

    loadTurnstileScript().then(render).catch((error) => {
      if (cancelledRef.current) return;
      onToken("");
      updateStatus("script-error", error?.message || "script_load");
    });

    return () => {
      cancelledRef.current = true;
      renderRef.current = null;
      resetWidgetRef.current = null;
      removeWidget();
    };
  }, [onToken, onStateChange, updateStatus]);

  useEffect(() => {
    if (!resetSignal || cancelledRef.current || statusRef.current === "loading") return undefined;
    onToken("");
    updateStatus("checking");
    resetWidgetRef.current?.({ fresh: true });
    return undefined;
  }, [onToken, resetSignal, updateStatus]);

  const retry = () => {
    onToken("");
    updateStatus("checking");
    if (resetWidgetRef.current?.({ fresh: true })) return;
    if (!nodeRef.current) return;
    if (widgetRef.current !== null && window.turnstile?.remove) {
      try { window.turnstile.remove(widgetRef.current); } catch (_) { /* re-render below */ }
    }
    widgetRef.current = null;
    nodeRef.current.replaceChildren();
    loadTurnstileScript().then(() => renderRef.current?.()).catch(() => updateStatus("script-error", "retry_load"));
  };

  if (!TURNSTILE_SITE_KEY) return <p className="nexus-auth-hint">Bot protection is configured by the Nexus deployment.</p>;
  const isRetryable = ["stuck", "error", "script-error", "timeout", "expired", "unsupported", "recovering"].includes(status);
  return <div className="nexus-turnstile" aria-label="Bot protection" data-turnstile-state={status} data-turnstile-error-code={errorCode || undefined}>
    <div ref={nodeRef} data-testid="turnstile-widget" className="nexus-turnstile-widget" />
    <div className="nexus-turnstile-feedback" aria-live="polite">
      <span data-testid="turnstile-status" className="nexus-turnstile-status">{friendlyTurnstileMessage(status)}</span>
      {isRetryable && <button type="button" className="nexus-turnstile-retry" data-testid="turnstile-retry" onClick={retry}>Retry security check</button>}
    </div>
  </div>;
}

export default function LoginScreen(props) {
  return TURNSTILE_ACTIVE ? <TurnstileLoginScreen {...props} /> : <LoginForm {...props} captcha={null} />;
}

function TurnstileLoginScreen(props) {
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaState, setCaptchaState] = useState("loading");
  const [captchaResetSignal, setCaptchaResetSignal] = useState(0);
  const clearCaptcha = () => {
    setCaptchaToken("");
    setCaptchaState("checking");
    setCaptchaResetSignal(value => value + 1);
  };
  return <LoginForm {...props} captcha={{ token: captchaToken, state: captchaState, resetSignal: captchaResetSignal, setToken: setCaptchaToken, setState: setCaptchaState, clear: clearCaptcha }} />;
}

function LoginForm({ anonKey, onLogin, sbLogin, sbSignUp, sbRecover, sbResendVerification, sbUpdatePassword, captcha }) {
  const hasRecoveryHash = window.location.hash.includes("type=recovery");
  const hashError = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("error_description");
  const [mode, setMode] = useState(() => hasRecoveryHash ? modes.reset : hashError ? modes.verify : modes.signIn);
  const [email, setEmail] = useState(localStorage.getItem("wn_email") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(hashError ? authMessage({ message: hashError }) : "");
  const [err, setErr] = useState("");
  const title = mode === modes.signUp ? "Create your Nexus account" : mode === modes.forgot ? "Recover access" : mode === modes.reset ? "Choose a new password" : mode === modes.verify ? "Verify your email" : "Welcome back";

  const captchaToken = captcha?.token || "";
  const captchaState = captcha?.state || "solved";
  const captchaResetSignal = captcha?.resetSignal || 0;
  const captchaRequired = Boolean(captcha);
  const captchaSolved = !captchaRequired || (captchaState === "solved" && Boolean(captchaToken));
  function clearCaptcha() {
    captcha?.clear();
  }
  function go(next) { setMode(next); setErr(""); setMessage(""); setPassword(""); setConfirm(""); clearCaptcha(); }

  async function submit(event) {
    event?.preventDefault(); setErr(""); setMessage("");
    if (mode !== modes.reset && mode !== modes.verify && !email) return setErr("Enter your email address.");
    if (mode === modes.signIn && !password) return setErr("Enter your password.");
    if (mode === modes.signUp && password !== confirm) return setErr("Passwords do not match.");
    if ((mode === modes.signUp || mode === modes.reset) && !isValidPassword(password)) return setErr("Use at least 8 characters, including a letter and a number.");
    if (captchaRequired && !captchaSolved) return setErr("The security check is still in progress. Complete it or retry the security check before continuing.");
    setLoading(true);
    try {
      const captchaArgs = captcha ? [captchaToken] : [];
      if (mode === modes.signIn) onLogin(await sbLogin(anonKey, email.trim(), password, ...captchaArgs));
      if (mode === modes.signUp) { await sbSignUp(anonKey, email.trim(), password, displayName.trim(), ...captchaArgs); localStorage.setItem("wn_email", email.trim()); setMode(modes.verify); setMessage("Your verification email is on its way. Open it to activate Nexus access, then return here to sign in."); }
      if (mode === modes.forgot) { await sbRecover(anonKey, email.trim(), ...captchaArgs); setMessage("If an account matches that address, a recovery email is on its way."); }
      if (mode === modes.reset) { await sbUpdatePassword(anonKey, password); setMessage("Your password has been updated. Sign in with your new password."); setMode(modes.signIn); }
      if (email) localStorage.setItem("wn_email", email.trim());
    } catch (error) {
      const friendly = authMessage(error);
      if (mode === modes.signIn && friendly.includes("Verify your email")) setMode(modes.verify);
      setErr(friendly);
    } finally { setLoading(false); if (captchaRequired) clearCaptcha(); }
  }

  async function resend() {
    setErr(""); setMessage("");
    if (!email) return setErr("Enter your email address first.");
    if (captchaRequired && !captchaSolved) return setErr("The security check is still in progress. Complete it or retry the security check before continuing.");
    setLoading(true);
    try { await sbResendVerification(anonKey, email.trim(), ...(captcha ? [captchaToken] : [])); setMessage("If an account matches that address, a fresh verification email is on its way."); }
    catch (error) { setErr(authMessage(error)); }
    finally { setLoading(false); clearCaptcha(); }
  }

  return <div className="login-container nexus-auth-shell" data-testid="auth-shell"><div className="nexus-auth-panel"><div className="nexus-auth-brand"><img src="/waffarha-logo.png" alt="Waffarha" /><span>Nexus intelligence</span></div><div className="nexus-auth-copy"><span className="nexus-auth-kicker">WAFFARHA NEXUS</span><h1>{title}</h1><p>{mode === modes.signIn ? "Your operating view for commercial intelligence and the workspaces your team uses." : mode === modes.verify ? "Email verification keeps Nexus accounts and module access tied to the right person." : "Secure access to the workspaces your team is entitled to use."}</p></div><form className="nexus-auth-card" data-testid="auth-form" onSubmit={submit}>
    {err && <div className="nexus-auth-alert error" role="alert">{err}</div>}{message && <div className="nexus-auth-alert success" role="status">{message}</div>}
    {mode === modes.verify ? <>
      <div className="nexus-auth-verify-mark" aria-hidden="true">✓</div><h2 className="nexus-auth-card-title">Check your inbox</h2><p className="nexus-auth-body">We sent a link to <strong>{email || "your email address"}</strong>. The link returns you to Nexus after verification.</p>
      <label>Email<input data-testid="auth-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" /></label>
      {captcha && <Turnstile key={mode} resetSignal={captchaResetSignal} onToken={captcha.setToken} onStateChange={captcha.setState} />}<button data-testid="auth-resend-verification" type="button" className="nexus-auth-submit" disabled={loading || !captchaSolved} onClick={resend}>{loading ? "Sending…" : "Resend verification email"}</button>
    </> : <>
      {mode !== modes.reset && <label>Email<input data-testid="auth-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" /></label>}
      {mode === modes.signUp && <label>Display name <span>(optional)</span><input data-testid="auth-display-name" value={displayName} onChange={e => setDisplayName(e.target.value)} autoComplete="name" /></label>}
      {mode !== modes.forgot && <label>{mode === modes.reset ? "New password" : "Password"}<input data-testid="auth-password" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete={mode === modes.signIn ? "current-password" : "new-password"} /></label>}
      {(mode === modes.signUp || mode === modes.reset) && <label>Confirm password<input data-testid="auth-confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" /></label>}
      {mode === modes.signUp && <div className="nexus-password-rules"><strong>Password requirements</strong><span>At least 8 characters</span><span>At least one letter and one number</span><span>Do not reuse a work password</span></div>}
      {captcha && (mode === modes.signUp || mode === modes.forgot || mode === modes.signIn) && <Turnstile key={mode} resetSignal={captchaResetSignal} onToken={captcha.setToken} onStateChange={captcha.setState} />}
      <button data-testid="auth-submit" className="nexus-auth-submit" disabled={loading || (captchaRequired && !captchaSolved)}>{loading ? "Working…" : mode === modes.signIn ? "Sign in" : mode === modes.signUp ? "Create account" : mode === modes.forgot ? "Send recovery email" : "Update password"}</button>
    </>}
    <div className="nexus-auth-links">{mode === modes.signIn && <><button data-testid="auth-forgot-password" type="button" onClick={() => go(modes.forgot)}>Forgot password?</button><button data-testid="auth-create-account" type="button" onClick={() => go(modes.signUp)}>Create account</button></>}{mode === modes.verify && <button data-testid="auth-back-to-sign-in" type="button" onClick={() => go(modes.signIn)}>Back to sign in</button>}{mode !== modes.signIn && mode !== modes.verify && <button data-testid="auth-back-to-sign-in" type="button" onClick={() => go(modes.signIn)}>Back to sign in</button>}</div>
  </form><p className="nexus-auth-footer">Authentication is handled by Supabase Auth. Module access is granted separately by Nexus.</p></div></div>;
}
